/**
 * NotificationProvider - Context provider for push notifications
 * Handles service worker registration and push notification initialization
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { pushNotificationService } from '@/services/pushNotification.service';
import { useAuth } from './AuthProvider';

interface NotificationContextType {
  isInitialized: boolean;
  isSupported: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    const initializeNotifications = async () => {
      try {
        // Check if push notifications are supported
        const supported = await pushNotificationService.isSupported();
        setIsSupported(supported);

        if (!supported) {
          console.log('Push notifications not supported in this browser');
          setIsInitialized(true);
          return;
        }

        // Register service worker for push notifications (only if not already registered)
        if ('serviceWorker' in navigator) {
          try {
            const registrations = await navigator.serviceWorker.getRegistrations();
            const existingRegistration = registrations.find(
              reg => reg.scope.includes('firebase-cloud-messaging-push-scope')
            );
            
            if (!existingRegistration) {
              const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
              console.log('Service Worker registered for notifications:', registration.scope);
            } else {
              console.log('Service Worker already registered:', existingRegistration.scope);
            }
          } catch (error) {
            console.error('Service Worker registration failed:', error);
          }
        }

        // Get permission state
        const permissionState = pushNotificationService.getPermissionState();
        
        // If permission is already granted, initialize push notifications
        if (permissionState.permission === 'granted') {
          const settings = pushNotificationService.getNotificationSettings();
          if (settings.enabled) {
            const userId = user?.user?.id;
            await pushNotificationService.initialize(userId);
          }
        }

        setIsInitialized(true);
      } catch (error) {
        console.error('Error initializing notification provider:', error);
        setIsInitialized(true);
      }
    };

    initializeNotifications();
  }, [user?.user?.id]);

  // Listen for notification click messages from service worker
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleMessage = (event: MessageEvent) => {
      console.log('[NotificationProvider] Message from SW:', event.data);
      if (event.data?.type === 'NOTIFICATION_CLICK' && event.data?.url) {
        console.log('[NotificationProvider] Navigating to:', event.data.url);
        const url = event.data.url;
        try {
          const parsedUrl = new URL(url);
          const path = parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
          // Use pushState + popstate event to trigger React Router navigation without reload
          window.history.pushState({}, '', path);
          window.dispatchEvent(new PopStateEvent('popstate'));
        } catch {
          window.history.pushState({}, '', url);
          window.dispatchEvent(new PopStateEvent('popstate'));
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
  }, []);

  // Re-initialize when user changes
  useEffect(() => {
    if (!isInitialized || !isSupported) return;

    const reinitializeForUser = async () => {
      const userId = user?.user?.id;
      const settings = pushNotificationService.getNotificationSettings();
      
      if (settings.enabled && userId) {
        console.log('Re-initializing push notifications for user:', userId);
        await pushNotificationService.initialize(userId);
      }
    };

    reinitializeForUser();
  }, [user?.user?.id, isInitialized, isSupported]);

  const contextValue: NotificationContextType = {
    isInitialized,
    isSupported
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  return context;
};
